/* RESPONSIBLE TEAM: team-proactive-support */
import Service from '@ember/service';
import { get } from 'embercom/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { COPILOT_SEAT_TYPE } from 'embercom/lib/settings/seats/constants';
import {
  FIN_AI_COPILOT_BASE_ID,
  FIN_FOR_PLATFORMS_BASE_ID,
  INCLUDED_TIERS_FROM_PRICING_ENDPOINT,
} from 'embercom/lib/billing';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import { ChargeModel } from 'embercom/models/billing/price/charge';

const MAX_ENDING_UNIT = 9999999999999;

export type TierPriceFormat = ChargeModel.FlatFee | ChargeModel.PerUnit;

type Tier = {
  priceFormat: TierPriceFormat;
  unitAmount: number;
  flatAmount: number;
  startingUnit: number;
  endingUnit: number;
};

export default class CopilotDataService extends Service {
  @service appService: any;
  @service customerService: any;

  @tracked private price: any;
  @tracked private usedAndPendingCopilotSeatCount: any;

  @tracked seatUsage = 0;

  get copilotData() {
    return {
      seatUsages: {
        contractedLimit: this.contractedLimit,
        pending: this.pending,
        used: this.seatUsage,
        price: this.seatPrice,
      },
      price: this.copilotPrice,
      tiers: this.tiers,
      isTiered: this.isTiered,
    };
  }

  async ensureSetup() {
    this.usedAndPendingCopilotSeatCount = await this.fetchUsedAndPendingCopilotSeatCount();
    this.seatUsage = Number(this.usedAndPendingCopilotSeatCount?.used || 0);
    this.price = await this.loadPrice();

    return this.copilotData;
  }

  async loadPrice() {
    try {
      await this.customerService.bulkLoadPrices([
        {
          planIds: [Number(this.planId)],
          includeTiers: INCLUDED_TIERS_FROM_PRICING_ENDPOINT,
        },
      ]);

      return this.customerService.prices.find((price: any) =>
        price.hasSamePlans([Number(this.planId)]),
      );
    } catch (e) {
      console.error('Failed to load Copilot price', e);
    }
  }

  updateSeatUsage(change: 'increment' | 'decrement') {
    if (change === 'increment') {
      this.seatUsage++;
    } else {
      this.seatUsage--;
    }
  }

  private async fetchUsedAndPendingCopilotSeatCount() {
    await this.customerService.ensureDataIsLoaded.perform({ fetchPrices: false });
    let usedAndPendingSeatCount = await get('/ember/settings/seats/used_and_pending_seat_count', {
      app_id: this.appService.app.id,
    });
    return usedAndPendingSeatCount?.[COPILOT_SEAT_TYPE];
  }

  get contractedLimit() {
    return Number(
      this.customerService.customer?.includedSeatCountPerType?.[COPILOT_SEAT_TYPE] || 0,
    );
  }

  get pending() {
    return Number(this.usedAndPendingCopilotSeatCount?.pending || 0);
  }

  get seatPrice() {
    return Number(this.customerService.customer?.pricePerSeatTypes?.[COPILOT_SEAT_TYPE] || 0);
  }

  private get planId() {
    return this.customerService.activePlanIds.includes(Number(FIN_FOR_PLATFORMS_BASE_ID))
      ? FIN_FOR_PLATFORMS_BASE_ID
      : FIN_AI_COPILOT_BASE_ID;
  }

  get copilotPrice() {
    if (!this.price) {
      return 0;
    }
    if (this.planId === FIN_FOR_PLATFORMS_BASE_ID) {
      return Number(this.price.getChargeForMetric(Metric.copilot_seat_count)?.per_unit_price ?? 0);
    }
    if (this.isTiered) {
      return Number(
        this.price.currentTier?.unit_amount || this.price.currentTier?.flat_amount || 0,
      );
    }
    return Number(this.price.perUnitPrice ?? 0);
  }

  get tiers(): Tier[] {
    if (!this.price?.tiers) {
      return [];
    }

    return Array.from(this.price.tiers).map((tier: any) => {
      return {
        priceFormat:
          tier.price_format === String(ChargeModel.FlatFee)
            ? ChargeModel.FlatFee
            : ChargeModel.PerUnit,
        unitAmount: Number(tier.unit_amount),
        flatAmount: Number(tier.flat_amount),
        startingUnit: Number(tier.starting_unit),
        endingUnit: Number(tier.ending_unit === MAX_ENDING_UNIT ? Infinity : tier.ending_unit),
      };
    });
  }

  get isTiered() {
    return this.price?.chargeModel === ChargeModel.Tiered;
  }
}

declare module '@ember/service' {
  interface Registry {
    copilotDataService: CopilotDataService;
    'copilot-data-service': CopilotDataService;
  }
}
